import filter from "lodash/filter";
import find from "lodash/find";
import get from "lodash/get";
import last from "lodash/last";

import { Update } from "@reduxjs/toolkit";
import { QueryStatus } from "@reduxjs/toolkit/query";

import { AppDispatch, Bet, RootState, Type } from "./types";
import { api } from "./store/api";
import { coreActions, getHashCode } from "./store/slices";
import {
  createGetBetsByParams,
  getAmount,
  getBalanceId,
  getBetCreateQueryArgsSplit,
  getNextId,
  getPrev,
} from "./selectors";
import { CREATE_BET_EVENT, sendPostMessage } from "./utils";

export const betsAddedByTypeThunk =
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (type: Type) => () => {
    /* DISABLED TEMPORARLY  */
    /*
    const state = getState();

    const nextId = getNextId(state);

    const bets = createGetBetsByParams([nextId], [type], [])(state);

    dispatch(betsAddedThunk(bets));
    */
  };

export const betsAddedByPersonalThunk =
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (line: number, type: "places" | "racers") => () => {
    /* DISABLED TEMPORARLY  */
    /*
    const state = getState();

    const nextId = getNextId(state);

    const bets = createGetBetsByParams([nextId], ["personal"], [])(state);

    const result: Array<Bet> = [];

    for (let i = 6; i > 0; i--) {
      const bet = bets.find((bet) =>
        type === "places"
          ? bet.racers[0] === i && bet.places[0] === line
          : bet.racers[0] === line && bet.places[0] === i
      );
      if (bet) {
        result.push(bet);
      }
    }

    dispatch(betsAddedThunk(result));
    */
  };

export const betsAddedByLineThunk =
  (line: number, invert: boolean) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    const nextId = getNextId(state);

    const bets = createGetBetsByParams([nextId], ["competition"], [])(state);
    const betsSelected = createGetBetsByParams(
      [nextId],
      ["competition"],
      ["confirmed", "pending", "selected"]
    )(state);

    const result: Array<Bet> = [];

    for (let i = 6; i > 0; i--) {
      const bet = bets.find((bet) =>
        invert
          ? bet.racers[0] === i && bet.racers[1] === line
          : bet.racers[0] === line && bet.racers[1] === i
      );

      if (bet) {
        switch (bet.status) {
          case "none": {
            const betsAdded = filter(result, { status: "none" });
            if (betsSelected.length + betsAdded.length < 13) {
              result.push(bet);
            }
            break;
          }
          case "selected": {
            result.push(bet);
            break;
          }
        }
      }
    }

    dispatch(betsAddedThunk(result));
  };

export const betsAddedThunk =
  (bets: Array<Bet>) => (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    const amount = getAmount(state);
    const balanceId = getBalanceId(state);

    const now = Date.now();

    const updates: Array<Update<Bet>> = [];

    bets.forEach((bet) => {
      const update: Update<Bet> = { id: bet.uuid, changes: {} };
      switch (bet.status) {
        case "none": {
          update.changes.added = now;
          update.changes.amount = amount;
          update.changes.balanceId = balanceId;
          update.changes.status = "selected";
          update.changes.updated = now;
          break;
        }
        case "selected": {
          if (bet.balanceId === balanceId) {
            update.changes.amount = amount + bet.amount;
          }
          break;
        }
      }
      updates.push(update);
    });

    dispatch(coreActions.betsUpdated(updates));
  };

export const betsAddedX2Thunk =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    const nextId = getNextId(state);

    const bets = createGetBetsByParams([nextId], [], ["selected"])(state);

    const updates: Array<Update<Bet>> = [];

    bets.forEach((bet) => {
      const update: Update<Bet> = { id: bet.uuid, changes: {} };
      update.changes.amount = bet.amount * 2;
      updates.push(update);
    });

    dispatch(coreActions.betsUpdated(updates));
  };

const getRepeatUpdates = (state: RootState): Array<Update<Bet>> => {
  const nextId = getNextId(state);
  const prev = getPrev(state);

  const bets = createGetBetsByParams([nextId], [], ["none"])(state);

  const now = Date.now();

  const updates: Array<Update<Bet>> = [];

  bets.forEach((bet) => {
    const gameId = get(last(prev), "gameId", 0);

    const hashCode = getHashCode(gameId, bet.type, bet.places, bet.racers);

    const repeat = find(prev, { hashCode });

    if (repeat) {
      const update: Update<Bet> = { id: bet.uuid, changes: {} };
      update.changes.added = now;
      update.changes.amount = repeat.amount;
      update.changes.balanceId = repeat.balanceId;
      update.changes.status = "selected";
      update.changes.updated = now;
      updates.push(update);
    }
  });

  return updates;
};

export const betsRepeatedThunk =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(coreActions.betsUpdated(getRepeatUpdates(getState())));
  };

/**
 * Отправка выбранных ставок: отдельный bet.create на каждый balanceId.
 */
export const betCreateSubmitThunk =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    const groups = getBetCreateQueryArgsSplit(getState());

    if (groups.length === 0) {
      return { hasError: false };
    }

    sendPostMessage(CREATE_BET_EVENT);

    if (groups.length > 1) {
      dispatch(coreActions.betCreateBatchStarted(groups.length));
    }

    const results = await Promise.all(
      groups.map((args) =>
        dispatch(api.endpoints.betCreate.initiate(args, { forceRefetch: true }))
      )
    );

    const hasError = results.some(
      (result) =>
        result.status === QueryStatus.rejected ||
        (result.status === QueryStatus.fulfilled && result.data?.error)
    );

    return { hasError };
  };

export const betsRepeatSubmitThunk =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(coreActions.betsUpdated(getRepeatUpdates(getState())));
    return dispatch(betCreateSubmitThunk());
  };

export const betRemovedThunk = (bet: Bet) => (dispatch: AppDispatch) => {
  const update: Update<Bet> = { id: bet.uuid, changes: {} };

  update.changes.added = 0;
  update.changes.amount = 0;
  update.changes.limit = false;
  update.changes.status = "none";

  dispatch(coreActions.betUpdated(update));
};

export const betsUndoThunk = () => (dispatch: AppDispatch) => {
  dispatch(coreActions.betsUndo());
};
